/**
 * Математика индикаторов.
 *
 * Живёт в общем пакете, а не в терминале, по одной причине: правило
 * должно считаться одинаково на графике и на сервере. Уведомление
 * обязано прийти при закрытом терминале, значит правило считает Core, —
 * а если бы математика осталась в коде отрисовки, у платформы было бы
 * два вычислителя и неизбежное расхождение между тем, что человек
 * видит, и тем, что ему присылают.
 *
 * Все функции возвращают массив той же длины, что и вход, где `null`
 * означает «данных ещё не хватило». Это важнее, чем кажется: если
 * прогревочный участок молча заполнить нулями или первым значением,
 * на графике появится ложная линия, по которой человек будет принимать
 * решения. Пусто — значит пусто.
 *
 * Здесь намеренно обычная плавающая арифметика, в отличие от агрегации
 * объёма в Core: индикатор — это среднее, а не сумма денег, и округлять
 * его к шагу инструмента бессмысленно.
 */
function filled(length) {
    return new Array(length).fill(null);
}
/** Простое скользящее среднее. */
export function sma(values, period) {
    const out = filled(values.length);
    if (period <= 0 || values.length < period)
        return out;
    let sum = 0;
    for (let i = 0; i < values.length; i += 1) {
        sum += values[i];
        if (i >= period)
            sum -= values[i - period];
        if (i >= period - 1)
            out[i] = sum / period;
    }
    return out;
}
/**
 * Экспоненциальное скользящее среднее.
 *
 * Затравка — SMA первых `period` значений: это классический вариант,
 * дающий тот же результат, что и в TradingView, а не «EMA от первого бара»,
 * которая заметно расходится на коротких историях.
 */
export function ema(values, period) {
    const out = filled(values.length);
    if (period <= 0 || values.length < period)
        return out;
    const k = 2 / (period + 1);
    let sum = 0;
    for (let i = 0; i < period; i += 1)
        sum += values[i];
    let previous = sum / period;
    out[period - 1] = previous;
    for (let i = period; i < values.length; i += 1) {
        previous = values[i] * k + previous * (1 - k);
        out[i] = previous;
    }
    return out;
}
/**
 * Сглаживание Уайлдера: коэффициент 1/period, а не 2/(period+1).
 * Используется в RSI и ATR — там, где автор индикатора задал именно его.
 */
function wilder(values, period, from) {
    const out = filled(values.length);
    if (period <= 0 || values.length < from + period)
        return out;
    let sum = 0;
    for (let i = from; i < from + period; i += 1)
        sum += values[i];
    let previous = sum / period;
    out[from + period - 1] = previous;
    for (let i = from + period; i < values.length; i += 1) {
        previous = (previous * (period - 1) + values[i]) / period;
        out[i] = previous;
    }
    return out;
}
/** Индекс относительной силы, 0..100. */
export function rsi(closes, period) {
    const out = filled(closes.length);
    if (period <= 0 || closes.length <= period)
        return out;
    const gains = [0];
    const losses = [0];
    for (let i = 1; i < closes.length; i += 1) {
        const change = closes[i] - closes[i - 1];
        gains.push(change > 0 ? change : 0);
        losses.push(change < 0 ? -change : 0);
    }
    // Первое изменение появляется только на баре 1, поэтому сглаживание
    // стартует оттуда же.
    const avgGain = wilder(gains, period, 1);
    const avgLoss = wilder(losses, period, 1);
    for (let i = 0; i < closes.length; i += 1) {
        const gain = avgGain[i];
        const loss = avgLoss[i];
        if (gain === null || gain === undefined || loss === null || loss === undefined)
            continue;
        // Падений не было вовсе — RSI упирается в 100, деления на ноль нет.
        out[i] = loss === 0 ? 100 : 100 - 100 / (1 + gain / loss);
    }
    return out;
}
/** Истинный диапазон бара. */
export function trueRange(highs, lows, closes) {
    const out = filled(highs.length);
    for (let i = 0; i < highs.length; i += 1) {
        const high = highs[i];
        const low = lows[i];
        if (i === 0) {
            out[i] = high - low;
            continue;
        }
        const previousClose = closes[i - 1];
        out[i] = Math.max(high - low, Math.abs(high - previousClose), Math.abs(low - previousClose));
    }
    return out;
}
/** Средний истинный диапазон. */
export function atr(highs, lows, closes, period) {
    const tr = trueRange(highs, lows, closes).map((value) => value ?? 0);
    return wilder(tr, period, 0);
}
/** Стандартное отклонение по скользящему окну (генеральное, как в TradingView). */
export function rollingStdDev(values, period) {
    const out = filled(values.length);
    if (period <= 0 || values.length < period)
        return out;
    for (let i = period - 1; i < values.length; i += 1) {
        let sum = 0;
        for (let j = i - period + 1; j <= i; j += 1)
            sum += values[j];
        const mean = sum / period;
        let variance = 0;
        for (let j = i - period + 1; j <= i; j += 1) {
            const delta = values[j] - mean;
            variance += delta * delta;
        }
        out[i] = Math.sqrt(variance / period);
    }
    return out;
}
/**
 * EMA поверх ряда с пропусками.
 *
 * Нужна для сигнальной линии MACD: сам MACD начинается не с нулевого бара,
 * и считать по нему EMA можно только с того места, где он появился.
 */
export function emaOfSparse(values, period) {
    const out = filled(values.length);
    const dense = [];
    const indices = [];
    for (let i = 0; i < values.length; i += 1) {
        const value = values[i];
        if (value === null || value === undefined)
            continue;
        dense.push(value);
        indices.push(i);
    }
    const computed = ema(dense, period);
    for (let i = 0; i < computed.length; i += 1) {
        const value = computed[i];
        if (value !== null && value !== undefined)
            out[indices[i]] = value;
    }
    return out;
}
//# sourceMappingURL=math.js.map